<template>
  <div class="task-employee-multiselect">
    <span v-if="label" :class="['task-employee-multiselect__label', { 'is-error': isError }]"
      >{{ label
      }}<span class="task-employee-multiselect__required-indicator" v-if="isRequired">*</span></span
    >
    <div class="task-employee-multiselect__content">
      <div
        class="task-employee-multiselect__avatar-row"
        v-if="visibleOptions.length"
        v-for="(elem, idx) of visibleOptions"
        :key="idx"
      >
        <TirPmDropdown
          class="task-employee-multiselect__dropdown"
          :options="optionsDropdown"
          :popoverPosition="PopoverPositionEnum.Top"
          @onOpen="openDropdown(idx)"
          @onClose="closeDropdown"
        >
          <template #target>
            <TirPmTooltipElement ref="$tooltip" width="max-content">
              <TirPmIconView
                class="task-employee-multiselect__avatar"
                :size="24"
                isRounded
                :isBordered="false"
                :colorCode="elem.AvatarColor"
                :imageLoadUrl="elem.ImageUrl"
                :key="elem.Id"
              >
                <template #mainSlot>
                  {{ elem.FirstNameLetterAndFirstLastNameLetter }}
                </template>
              </TirPmIconView>
              <template #content>
                <div class="task-employee-multiselect__popover-title">
                  {{ elem.FirstName }} {{ elem.MiddleName }} {{ elem.LastName }}
                </div>
              </template>
            </TirPmTooltipElement>
          </template>
          <template #optionList="{ option }">
            <div
              class="task-employee-multiselect__dropdown-title"
              @click="openRemoveModal(elem.Id, elem.FirstName, elem.LastName)"
            >
              {{ option.text }} {{ elem.FirstName }} {{ elem.LastName[0] }}.
            </div>
          </template>
        </TirPmDropdown>
        <TirPmModal
          title="Открепить сотрудника"
          :is-visible="removeModalState"
          :isWithBack="false"
          :isCloseOverlay="true"
          @close="closeModal"
        >
          <template #content>
            <div class="remove-task-modal__content">
              {{ dataEmployee.firstName }} {{ dataEmployee.lastName[0] }}. больше не сможет
              взаимодействовать с тикетом и получать уведомления об изменениях
            </div>
          </template>
          <template #footer>
            <TirPmButton
              :variant="TirPmButtonVariantEnum.PrimaryState"
              @click="unSelectEmployee(dataEmployee.id)"
            >
              Открепить
            </TirPmButton>
            <TirPmButton :variant="TirPmButtonVariantEnum.SecondaryState" @click="closeModal">
              Отмена
            </TirPmButton>
          </template>
        </TirPmModal>
      </div>
      <TirPmIconView
        class="task-employee-multiselect__avatar"
        v-if="countHiddenOptions"
        :size="24"
        isRounded
        :isBordered="false"
      >
        <template #mainSlot> +{{ countHiddenOptions }} </template>
      </TirPmIconView>
      <div v-if="editing" class="task-employee-multiselect__button">
        <TirPmMultiselect
          :options="options"
          :selected-options="selectedOptions"
          :style="{ width }"
          :popover-position="PopoverPositionEnum.Right"
          width="100%"
          @onOpen="checkOpenMultiselect"
        >
          <template #target>
            <TirPmButton
              v-if="!modelValue.length"
              :size="TirPmButtonSizeEnum.Small"
              :isPressed="isOpenMultiselect"
              :variant="TirPmButtonVariantEnum.SecondaryState"
            >
              Добавить
              <template #leftSlot>
                <PlusIcon />
              </template>
            </TirPmButton>
            <TirPmButton
              v-else
              :size="TirPmButtonSizeEnum.Small"
              :variant="TirPmButtonVariantEnum.LinkState"
              :isPressed="isOpenMultiselect"
            >
              <template #leftSlot>
                <PlusIcon style="color: var(--text-primary, #272d37)" />
              </template>
            </TirPmButton>
          </template>
          <template #optionList="{ option }">
            <div class="task-employee-multiselect__options-list" @click="selectEmployee(option.Id)">
              <TirPmIconView
                class="task-employee-multiselect__avatar"
                :size="24"
                isRounded
                :isBordered="false"
                :colorCode="option.AvatarColor"
                :imageLoadUrl="option.ImageUrl"
              >
                <template #mainSlot>
                  {{ option.FirstNameLetterAndFirstLastNameLetter }}
                </template>
              </TirPmIconView>
              <span v-if="option.Id === CurrentUser.Id" style="font-weight: 500"
                >Назначить себя</span
              >
              <span v-else>{{ option.FirstAndLastName }}</span>
            </div>
          </template>
          <template #left>
            <slot name="left" />
          </template>
          <template #selectedContent>
            <div class="task-employee-multiselect__selected-content">
              <TirPmSelectBadge
                v-for="option in selectedOptions"
                is-closable
                @close="unSelectEmployee(option.Id)"
              >
                <template #sign>
                  {{ option.FirstNameLetterAndFirstLastNameLetter }}
                </template>
                <template #iconViewAddon>
                  <TirPmIconView
                    class="task-employee-multiselect__avatar"
                    :size="24"
                    isRounded
                    :isBordered="false"
                    :colorCode="option.AvatarColor"
                    :imageLoadUrl="option.ImageUrl"
                  >
                    <template #mainSlot>
                      {{ option.FirstNameLetterAndFirstLastNameLetter }}
                    </template>
                  </TirPmIconView>
                </template>
              </TirPmSelectBadge>
            </div>
          </template>
        </TirPmMultiselect>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, PropType, ref, watch } from "vue";
import { UserModel } from "../../models";
import {
  TirPmMultiselect,
  TirPmSelectBadge,
  TirPmIconView,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmPopover,
  PopoverPositionEnum,
  PopoverColorEnum,
  TirPmModal,
  TirPmDropdown,
  TirPmTooltipElement,
} from "tir-components";
import { EmployeeMultiselectOptionModel } from "./models";
import { PlusIcon } from "tir-style-system/icons/outline";
import { CurrentUser } from "tir-pm-user-core";
import { useModal } from "tir-pm-toolkit";

const emit = defineEmits<{
  /**Событие обновление выбранных сотрудников */
  (e: "update:modelValue", value: Array<number>): void;
}>();

const props = defineProps({
  /**Выбранные сотрудники */
  modelValue: {
    type: Array as PropType<Array<number>>,
    default: [],
  },
  /**Возможность редактировать */
  editing: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  /**Сотрудники для выбора */
  employees: {
    type: Array as PropType<Array<UserModel>>,
    requred: true,
  },
  /**Ширина */
  width: {
    type: String,
    default: "100%",
  },
  /**Лейбл */
  label: {
    type: String,
  },
  /**Ошибка */
  isError: {
    type: Boolean,
    default: false,
  },
  /**Обязательное поле */
  isRequired: {
    type: Boolean,
    default: false,
  },
});

/**Ссылка на элемент подсказки */
const $tooltip = ref<InstanceType<typeof TirPmTooltipElement>>();
const isOpened = ref(false);

/**Опции Dropdown */
const optionsDropdown = [
  {
    text: "Открепить",
  },
];

/**Опции для мультиселекта */
const options = computed(() => {
  return props.employees.map((employee) => {
    return new EmployeeMultiselectOptionModel({
      Id: employee.Id,
      FirstName: employee.FirstName,
      LastName: employee.LastName,
      MiddleName: employee.MiddleName,
      ImageUrl: employee.ImageUrl,
      AvatarColor: employee.AvatarColor,
    });
  });
});

/**Выбранные опции */
const selectedOptions = computed(() =>
  options.value.filter((option) => props.modelValue.some((employeeId) => option.Id === employeeId)),
);

/**Кол-во видимых опций */
const countVisibleOptions = 4;
const isOpenMultiselect = ref();
/**Состояние открытой модели удаления сотрудника */
const removeModalState = ref();

const checkOpenMultiselect = () => {
  isOpenMultiselect.value = !isOpenMultiselect.value;

  // event.stopPropagation();//TODO непонял за чем  и так пашет
  document.addEventListener("click", () => {
    isOpenMultiselect.value = false;
  });
};
/**Видимые выбранные опции */
const visibleOptions = computed(() => selectedOptions.value.slice(0, countVisibleOptions));
/**Кол-во скрытых выбранных опций */
const countHiddenOptions = computed(
  () => selectedOptions.value.length - visibleOptions.value.length,
);

/**При открытии Dropdown */
const openDropdown = (idx) => {
  $tooltip.value[idx]?.close();
  setTimeout(() => {
    isOpened.value = true;
  }, 100);
};

/**При закрытии Dropdown */
const closeDropdown = () => {
  isOpened.value = false;
};

/**Данные сотрудника */
const dataEmployee = ref();

/**Открытие модалки для открепления сотрудника */
const openRemoveModal = (id: number, firstName: string, lastName: string) => {
  dataEmployee.value = {
    id: id,
    firstName: firstName,
    lastName: lastName,
  };
  removeModalState.value = true;
};

const closeModal = () => {
  removeModalState.value = false;
};
const selectEmployee = (employeeId: number) => {
  emit("update:modelValue", [...props.modelValue, employeeId]);
};

const unSelectEmployee = (employeeId: number) => {
  emit(
    "update:modelValue",
    props.modelValue.filter((_employeeId) => employeeId !== _employeeId),
  );
};
</script>

<style lang="scss" scoped>
.remove-task-modal__content {
  margin: 0.75rem 0;
}
.task-employee-multiselect {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  &__dropdown-title {
    padding: 0.75rem;
    font-size: 1rem;
  }

  &__popover-title {
    font-size: 0.75rem;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 0.313rem;
  }

  &__label {
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
    color: var(--text-secondary, #686c73);

    &.is-error {
      color: var(--text-negative, #ec2e14);
    }
  }
  &__options-list {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem;
  }
  &__selected-content {
    display: flex;
    gap: 0.125rem;
    flex-wrap: wrap;
    max-width: 25rem;
  }
  &__avatar-row {
    display: flex;
    align-items: center;
    margin-left: -0.125rem;
    cursor: pointer;
    &:first-child {
      margin-left: 0;
    }
  }
  &__required-indicator {
    color: var(--text-negative, #ec2e14);
  }
}
</style>
